import React, { FunctionComponent, useCallback, useContext } from 'react';
import {
  PageHeader,
  Content,
  ResponsivePadding,
  ResponsiveMargin,
} from '../../components';
import {
  RegistrationStore,
  updateRegistration,
  BrandStore,
} from '../../context';
import { coreapi } from '../../api';
import {
  useAsyncEffect,
  useToaster,
  useAuthToken,
  useNextApplicationPath,
  useRedirectToNextApplicationPathIfPageIsHidden,
  useRequiredDocumentGate,
} from '../../hooks';
import { history } from '../../history';
import { Documents } from './Documents';
import { PrimaryButton } from '@stax-core/truffle-components';

export const DocumentsPage: FunctionComponent = () => {
  const { dispatch } = useContext(RegistrationStore);
  const {
    state: { brand },
  } = useContext(BrandStore);
  const nextPath = useNextApplicationPath();
  const { authToken } = useAuthToken();
  const { toaster, toast } = useToaster();
  const { regDocumentsHeader, regDocumentsBody } = brand || {};

  // Gate progression on required documents: the merchant can't continue until
  // every `is_required` document has at least one uploaded file (SPX-2051).
  const { showRequiredErrors, handleMissingRequiredChange, guardProgression } =
    useRequiredDocumentGate();

  const handleContinue = useCallback(() => {
    if (!guardProgression()) return;
    history.push(nextPath!);
  }, [guardProgression, nextPath]);

  useAsyncEffect(async () => {
    try {
      const res = await coreapi.get(authToken!, '/team/registration');
      dispatch(updateRegistration(res));
    } catch (error) {
      toaster(
        toast.error(error, 'There was a problem fetching your application.'),
      );
    }
  }, [authToken]);

  useRedirectToNextApplicationPathIfPageIsHidden('Documents/Statements');

  return (
    <>
      <PageHeader
        header={regDocumentsHeader}
        id="documents-page-header"
        data-testid="documents-page-header"
        description={<p>{regDocumentsBody}</p>}
      />
      <Content>
        <Documents
          accept="image/jpeg,image/jpg,image/png,application/pdf,.pdf,.jpeg,.jpg,.png"
          showRequiredErrors={showRequiredErrors}
          onMissingRequiredChange={handleMissingRequiredChange}
        />
      </Content>
      <Content>
        <ResponsivePadding>
          <ResponsiveMargin mobile="1rem 0" desktop="3rem 0rem">
            <PrimaryButton
              style={{ width: '100%' }}
              id="documents-continue-button"
              data-testid="documents-continue-button"
              trackingId="documents-continue-button"
              onClick={handleContinue}
            >
              <span>Continue</span>
            </PrimaryButton>
          </ResponsiveMargin>
        </ResponsivePadding>
      </Content>
    </>
  );
};
